import React from 'react';
import { Navigate } from 'react-router';
import { BrowserRouter, Route, Routes } from 'react-router';

import { ProgressStackProvider } from '../../shared/context/ProgressStackContext';
import { StorageContext } from '../../shared/storageKey';

import { OAuthProvider, OAuthSilentCallback } from './OAuthProvider';
import { About } from './features/About';
import { Agreement } from './features/Agreement';
import { AutoClosePage } from './features/AutoClosePage';
import { ExplicitConsent } from './features/ExplicitConsent';
import { Faq } from './features/Faq';
import { ImageList } from './features/ImageList/ImageList';
import { Landing } from './features/Landing';
import { LandingLayout, Layout, ProtectedRoute } from './features/Layout';
import { ProgressStackTray } from './features/Layout/ProgressStackTray';
import { IdentificationProtectedRoute } from './features/Layout/ProtectedRoute';
import {
  MobilePaymentCallback,
  MobilePaymentCdCallback,
  MobilePaymentAdminStudiesCallback,
} from './features/MobilePaymentCallback';
import { MyPage } from './features/MyPage';
import { Notifications } from './features/Notifications';
import { Payments } from './features/Payments';
import { QrCodePrepare, QrCodePreview } from './features/QrCode';
import ReceiveCaseAgreement from './features/ReceiveCaseAgreement';
import { Redirect } from './features/RedirectPage';
import { RequestImages } from './features/RequestImages/RequestImages';
import { SendImages } from './features/SendImages/SendImages';
import {
  ShareToPatientForward,
  ShareToPatientNonMember,
  ShareToPatientPreview,
} from './features/ShareToPatient';
import {
  StoreDelegateAgreement,
  StoreDelegateVerification,
} from './features/StoreDelegate';
import { Terms } from './features/Terms';
import { PrivacyPolicy } from './features/Terms';
import { TermsOfService } from './features/Terms';
import { TwoFactorAuthentication } from './features/TwoFactorAuthentication';
import { UploadImages } from './features/UploadImages/UploadImages';
import { UploadedImages } from './features/UploadedImages';
import { useWindowSize, WindowSizeContext } from './hooks/windowSizeHook';

const AppRoute = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/auto-close" element={<AutoClosePage />} />
      <Route
        path="/receive-case-agreement/:id"
        element={<ReceiveCaseAgreement />}
      />
      <Route
        path="/share-to-patient/:id"
        element={<ShareToPatientNonMember />}
      />
      <Route
        path="/share-to-patient/:id/forward"
        element={<ShareToPatientForward />}
      />
      <Route
        path="/share-to-patient/:id/preview"
        element={<ShareToPatientPreview />}
      />
      <Route path="/qr/:id" element={<QrCodePrepare />} />
      <Route path="/qr/:id/preview" element={<QrCodePreview />} />
      <Route
        path="/store-delegate/:id/agreement"
        element={<StoreDelegateAgreement />}
      />
      <Route
        path="/store-delegate/:id/verification"
        element={<StoreDelegateVerification />}
      />
      <Route path="/explicit-consent/:uuid" element={<ExplicitConsent />} />
      <Route path="/oauth-silent-callback" element={<OAuthSilentCallback />} />
      <Route path="/redirect" element={<Redirect />} />
      <Route element={<LandingLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/about" element={<About />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/two-factor-login" element={<TwoFactorAuthentication />} />
      </Route>
      <Route
        path="/mobile-payment-callback/local-issuance-online"
        element={<MobilePaymentAdminStudiesCallback />}
      />
      <Route element={<ProtectedRoute />}>
        <Route
          path="/mobile-payment-callback"
          element={<MobilePaymentCallback />}
        />
        <Route
          path="/mobile-payment-callback/cd"
          element={<MobilePaymentCdCallback />}
        />
        <Route element={<Layout />}>
          <Route path="/images-list" element={<ImageList />} />
          <Route element={<IdentificationProtectedRoute />}>
            <Route path="/request-images" element={<RequestImages />} />
          </Route>
          <Route path="/wait-for-images" element={<>Wait for Images</>} />
          <Route path="/send-images" element={<SendImages />} />
          <Route path="/upload-images" element={<UploadImages />} />
          <Route path="/my-page" element={<MyPage />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/requested-images" element={<Payments />} />
          <Route path="/uploaded-images" element={<UploadedImages />} />
          <Route path="/agreement" element={<Agreement />} />
        </Route>
      </Route>
      {/*
      {Object.values(AgreementType).map(type => (
        <Route
          key={type}
          path={`/agreement/${type}`}
          element={<Agreement type={type} />}
        />
      ))}
      */}
      <Route path="*" element={<Navigate to="/" />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/terms-v2/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-v2/terms-of-service" element={<TermsOfService />} />
    </Routes>
  </BrowserRouter>
);

function App() {
  const windowSize = useWindowSize();
  return (
    <WindowSizeContext.Provider value={windowSize}>
      <StorageContext.Provider
        value={{
          removeItem: window.localStorage.removeItem.bind(localStorage),
          clear: window.localStorage.clear.bind(localStorage),
          setItem: window.localStorage.setItem.bind(localStorage),
          getItem: key => Promise.resolve(localStorage.getItem(key)),
        }}>
        <OAuthProvider>
          <ProgressStackProvider>
            <AppRoute />
            <ProgressStackTray />
          </ProgressStackProvider>
        </OAuthProvider>
      </StorageContext.Provider>
    </WindowSizeContext.Provider>
  );
}

export default App;
